import { HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { CoreActions } from '@next/core-lib';
import { DIALOG_RETURN_TYPES, DialogService } from '@next/core-lib/dialog';
import { LocaleService } from '@next/core-lib/i18n';
import { NotificationActions } from '@next/core-lib/notification';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, select, Store } from '@ngrx/store';
import { EMPTY, Observable, of } from 'rxjs';
import {
  catchError,
  exhaustMap,
  finalize,
  map,
  mergeMap,
  startWith,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs/operators';

import * as animalGroupActions from './animal-group.actions';

import { getSelectedAnimalGroup, getSelectedAnimalGroupId } from './animal-group.selectors';
import { loadBarnsRequest } from '../../settings/store/actions/settings.actions';
import {
  AnimalGroup,
  AnimalGroupModuleState,
  UpsertAnimalGroupResponse,
} from '../models/animal-group.model';

import { AnimalGroupService } from '../services/animal-group.service';

interface ConfirmDialogData {
  text?: string;
}

@Injectable()
export class AnimalGroupEffects {
  constructor(
    private actions$: Actions,
    private readonly store: Store<AnimalGroupModuleState>,
    private readonly router: Router,
    private readonly ngZone: NgZone,
    private readonly dialogService: DialogService,
    private readonly animalGroupService: AnimalGroupService,
    @Inject(LocaleService) private readonly locale: LocaleService,
  ) {}

  loadData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(animalGroupActions.loadData),
      switchMap(() => [
        animalGroupActions.clearAnimalGroupState(),
        animalGroupActions.loadAnimalGroupsRequest(),
        loadBarnsRequest(),
      ]),
    ),
  );

  loadAnimalGroups$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(animalGroupActions.loadAnimalGroupsRequest),
      mergeMap(() =>
        this.animalGroupService.getAnimalGroups().pipe(
          map((animalGroups: AnimalGroup[]) =>
            animalGroups.sort((c1: AnimalGroup, c2: AnimalGroup) => c2.id - c1.id),
          ),
          withLatestFrom(this.store.select(getSelectedAnimalGroupId)),
          switchMap(([animalGroups]) =>
            of(animalGroupActions.loadAnimalGroupsSuccess({ animalGroups })),
          ),
          catchError((error: HttpErrorResponse) =>
            of(
              animalGroupActions.loadAnimalGroupsFailed({ error }),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  deleteAnimalGroupRequest$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(animalGroupActions.deleteAnimalGroupRequest),
      mergeMap(({ animalGroup }) =>
        this.animalGroupService.deleteAnimalGroup(animalGroup.id).pipe(
          map(() => animalGroupActions.deleteAnimalGroupSuccess({ animalGroup })),
          catchError((error: HttpErrorResponse) =>
            of(
              animalGroupActions.deleteAnimalGroupFailed({ error }),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  deleteAnimalGroupSuccess$: Observable<Action> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(animalGroupActions.deleteAnimalGroupSuccess),
        tap(() => {
          this.ngZone.run(() => this.router.navigate(['/animal-group'])).then();
        }),
      ),
    { dispatch: false },
  );

  createAnimalGroup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(animalGroupActions.createAnimalGroup),
      map(({ animalGroup }) => animalGroupActions.upsertAnimalGroupRequest({ animalGroup })),
    ),
  );

  saveSelectedAnimalGroup$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(
        animalGroupActions.upsertAnimalGroupGeneral,
        animalGroupActions.addFeedadviceApplication,
        animalGroupActions.editFeedadviceApplication,
        animalGroupActions.deleteFeedadviceApplication,
      ),
      switchMap((_) => this.store.pipe(select(getSelectedAnimalGroup), take(1))),
      map((animalGroup: AnimalGroup) =>
        animalGroupActions.upsertAnimalGroupRequest({ animalGroup }),
      ),
    ),
  );

  upsertAnimalGroupRequest$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(animalGroupActions.upsertAnimalGroupRequest),
      exhaustMap(({ animalGroup }) =>
        this.animalGroupService.upsertAnimalGroup(animalGroup).pipe(
          map((response: UpsertAnimalGroupResponse) =>
            animalGroupActions.upsertAnimalGroupSuccess({
              animalGroup: {
                ...animalGroup,
                id: response.id,
              },
            }),
          ),
          catchError((error: HttpErrorResponse) =>
            of(
              animalGroupActions.upsertAnimalGroupFailed({ error }),
              animalGroupActions.loadAnimalGroupsRequest(),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  upsertAnimalGroupSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(animalGroupActions.upsertAnimalGroupSuccess),
        map((action) => action.animalGroup),
        tap((animalGroup) => {
          const urlParts = this.router.url.split('?');
          const url = urlParts.length > 0 ? urlParts[0] : this.router.url;
          if (url.indexOf('/animalGroup/add') >= 0) {
            this.store.dispatch(animalGroupActions.loadAnimalGroupsRequest());
            this.ngZone.run(() => this.router.navigate(['/animal-group', animalGroup.id])).then();
          }
        }),
      ),
    { dispatch: false },
  );

  deleteAnimalGroupWithConfirmation$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(animalGroupActions.deleteAnimalGroupWithConfirmation),
      switchMap((action) =>
        this.dialogService.confirmWarning(this.getDeleteDialogConfimation('animal-group')).pipe(
          switchMap(
            (result) =>
              (result.status === DIALOG_RETURN_TYPES.confirm && [
                animalGroupActions.deleteAnimalGroupRequest({
                  animalGroup: action.animalGroup,
                }),
              ]) ||
              EMPTY,
          ),
        ),
      ),
    ),
  );

  deleteFeedadviceApplicationWithConfirmation$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(animalGroupActions.deleteFeedadviceApplicationWithConfirmation),
      switchMap((action) =>
        this.dialogService
          .confirmWarning(this.getDeleteDialogConfimation('feedadvice-application'))
          .pipe(
            switchMap(
              (result) =>
                (result.status === DIALOG_RETURN_TYPES.confirm && [
                  animalGroupActions.deleteFeedadviceApplication({
                    animalGroupId: action.animalGroupId,
                    id: action.id,
                  }),
                ]) ||
                EMPTY,
            ),
          ),
      ),
    ),
  );

  private getDeleteDialogConfimation(type: string): ConfirmDialogData {
    return {
      text: this.locale.translate(`animal-group.delete-${type}`),
    };
  }
}
